import React from 'react';
import styled from 'styled-components';

import { type RendererRectOffsets, useRendererRect } from './useRendererRect';

import type { AspectRatio } from '@src/types.ts/video';

interface PlayerOverlayProps {
  aspectRatio?: AspectRatio;
  dynamicScaleFactor?: boolean;
  show?: boolean;
  children?: React.ReactNode;
  borderStyle?: string;
  rendererElement?: HTMLDivElement;
  dimContainerAspectRatio?: number;
  offsets?: RendererRectOffsets;
}

const OverlayComponent = styled.div<{ borderStyle: string }>`
  position: absolute;
  border: ${p => p.borderStyle};
  pointer-events: none;
  z-index: 10000;
  box-shadow: 0 0 0 10000px var(--overlay-dark);
`;

const DimContainer = styled.div<{ show: boolean }>`
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  padding: 4px;
  z-index: 9999;
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

export const BORDER_STYLE_DEFAULT = '1px solid var(--border)';

const DEFAULT_OFFSETS: RendererRectOffsets = {
  CONTAINER_LEFT_OFFSET: 0,
  CONTAINER_TOP_OFFSET: 0,
  CONTAINER_HEIGHT_OFFSET: 0,
  CONTAINER_WIDTH_OFFSET: 0,
  OVERLAY_TOP_OFFSET: 0,
  OVERLAY_LEFT_OFFSET: 0,
};

export const PlayerOverlay: React.FC<PlayerOverlayProps> = ({
  aspectRatio,
  show = true,
  children,
  dynamicScaleFactor = true,
  borderStyle = BORDER_STYLE_DEFAULT,
  rendererElement,
  dimContainerAspectRatio = 16 / 9,
  offsets = DEFAULT_OFFSETS,
}) => {
  const rect = useRendererRect(rendererElement, aspectRatio, {
    dimContainerAspectRatio,
    dynamicScaleFactor,
    offsets,
  });

  return (
    <DimContainer
      data-testid="creator-studio-player-overlay"
      show={show}
      style={{
        left: `${rect.containerLeft}px`,
        top: `${rect.containerTop}px`,
        width: `${rect.containerWidth}px`,
        height: `${rect.containerHeight}px`,
      }}
    >
      <OverlayComponent
        style={{
          left: `${rect.overlayLeft}px`,
          top: `${rect.overlayTop}px`,
          width: `${rect.overlayWidth}px`,
          height: `${rect.overlayHeight}px`,
        }}
        borderStyle={borderStyle}
      >
        {children}
      </OverlayComponent>
    </DimContainer>
  );
};
